import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Avatar from '@mui/material/Avatar';
import { useAuth } from './AuthContext';

export default function SignIn() {
  const { signIn, forceChangePassword, forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Force change password state
  const [mode, setMode] = useState('signin'); // 'signin' | 'force-change' | 'forgot' | 'reset'
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [session, setSession] = useState(null);
  const [resetCode, setResetCode] = useState('');
  const [success, setSuccess] = useState('');

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signIn({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      if (err.challenge === 'NEW_PASSWORD_REQUIRED') {
        setSession(err.session);
        setMode('force-change');
        setError('');
      } else if (err.challenge === 'PASSWORD_RESET_REQUIRED') {
        // Auto-send reset code and switch to reset flow
        try { await forgotPassword({ email }); } catch (_) { /* ignore */ }
        setMode('reset');
        setSuccess('Your password needs to be reset. A verification code has been sent to your email.');
      } else {
        const message = err.response?.data?.detail || err.response?.data?.error || err.message || 'Sign in failed. Please try again.';
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceChange = async (e) => {
    e.preventDefault();
    if (!newPassword) { setError('Please enter a new password.'); return; }
    if (newPassword !== confirmNewPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await forceChangePassword({ email, newPassword, session });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    setError('');
    try {
      await forgotPassword({ email });
      setMode('reset');
      setSuccess('A verification code has been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) { setError('Please enter the code and new password.'); return; }
    if (newPassword !== confirmNewPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await confirmForgotPassword({ email, code: resetCode, newPassword });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const renderTitle = () => {
    switch (mode) {
      case 'force-change': return 'Set New Password';
      case 'forgot': return 'Forgot Password';
      case 'reset': return 'Reset Password';
      default: return 'Sign In';
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {renderTitle()}
        </Typography>

        <Paper
          component="form"
          onSubmit={
            mode === 'signin' ? handleSubmit :
            mode === 'force-change' ? handleForceChange :
            mode === 'forgot' ? handleForgotPassword :
            handleResetPassword
          }
          noValidate
          sx={{ mt: 3, p: 3, width: '100%' }}
          elevation={2}
        >
          {mode === 'signin' && (
            <>
              <TextField margin="normal" required fullWidth label="Email Address" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
              <TextField margin="normal" required fullWidth label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link component="button" type="button" variant="body2" onClick={() => { setMode('forgot'); setError(''); }}>
                  Forgot Password?
                </Link>
                <Link component={RouterLink} to="/signup" variant="body2">
                  Don't have an account? Sign Up
                </Link>
              </Box>
            </>
          )}

          {mode === 'force-change' && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>You need to set a new password before continuing.</Alert>
              <TextField margin="normal" required fullWidth label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={loading} autoFocus />
              <TextField margin="normal" required fullWidth label="Confirm New Password" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} disabled={loading} />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                {loading ? 'Setting Password...' : 'Set New Password'}
              </Button>
            </>
          )}

          {mode === 'forgot' && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>Enter your email address and we'll send you a verification code to reset your password.</Typography>
              <TextField margin="normal" required fullWidth label="Email Address" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component="button" type="button" variant="body2" onClick={() => { setMode('signin'); setError(''); }}>
                  Back to Sign In
                </Link>
              </Box>
            </>
          )}

          {mode === 'reset' && (
            <>
              <TextField margin="normal" required fullWidth label="Verification Code" value={resetCode} onChange={(e) => setResetCode(e.target.value)} disabled={loading} autoFocus />
              <TextField margin="normal" required fullWidth label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={loading} />
              <TextField margin="normal" required fullWidth label="Confirm New Password" type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} disabled={loading} />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component="button" type="button" variant="body2" onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}>
                  Back to Sign In
                </Link>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess('')} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
    </Container>
  );
}
